/**
 * Unmarried partners must have lived together in a relationship akin to
 * marriage or civil partnership for at least two years (GEN.1.2(iv)).
 *
 * The period is measured to the date of application, not to `as_of` — the
 * projected application date is what the Home Office assesses. Where the
 * period is short but will complete, the outcome carries the date it does
 * (NR-1): a computed date, never "come back later".
 */
import { addMonthsISO, compareISODate } from "../dates.js";
import type { EvaluatePredicate } from "./predicate.js";

export const cohabitation_two_years: EvaluatePredicate = (facts, rule, ctx) => {
  const years = numeric(ctx.paramFigure(rule, "years_figure"));
  const since = facts.relationship.cohabiting_since;

  if (since === null) {
    return {
      status: "PENDING_EVIDENCE",
      notes: ["The date the couple started living together is needed."],
    };
  }

  const qualifies = addMonthsISO(since, years * 12);
  const assessedAt = facts.intended_application_date ?? facts.as_of;

  if (compareISODate(assessedAt, qualifies) >= 0) return { status: "MET" };

  return {
    status: "NOT_MET",
    eligible_from: qualifies,
    notes: [
      "The rules ask for at least two years of living together for unmarried partners. The date that period completes is shown.",
    ],
  };
};

function numeric(value: number | string): number {
  if (typeof value !== "number") throw new Error(`Expected a numeric figure, got: ${value}`);
  return value;
}
